import { useEffect, useState } from "react";
import { Copy, Download, Loader2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { DISCLOSURE, cardCls } from "@/lib/creators";
import { dailyCaptions } from "@/lib/captions";

const W = 1080, H = 1920;

function wrap(ctx, text, max) {
  const out = [];
  let line = "";
  for (const w of text.split(/\s+/)) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > max && line) { out.push(line); line = w; } else line = t;
  }
  if (line) out.push(line);
  return out;
}

async function drawStory(sign, text, name, link) {
  await document.fonts?.ready;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = "#04060B"; x.fillRect(0, 0, W, H);
  for (let i = 0; i < 150; i++) {
    x.fillStyle = `rgba(247,244,238,${0.15 + ((i * 37) % 70) / 100})`;
    x.beginPath(); x.arc((i * 997) % W, (i * 613) % H, ((i * 7) % 3) + 1, 0, Math.PI * 2); x.fill();
  }
  x.strokeStyle = "#D4AF37"; x.lineWidth = 4; x.strokeRect(48, 48, W - 96, H - 96);
  x.textAlign = "center";
  x.fillStyle = "#D4AF37"; x.font = "36px 'Cormorant Garamond', Georgia, serif"; x.fillText(`${name.toUpperCase()}'S DAILY HOROSCOPE`, W / 2, 220);
  x.fillStyle = "#F7F4EE"; x.font = "300 120px 'Cormorant Garamond', Georgia, serif"; x.fillText(sign, W / 2, 420);
  x.fillStyle = "#A7B0C0"; x.font = "34px Georgia, serif";
  x.fillText(new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" }), W / 2, 490);
  x.fillStyle = "#E8DCC4"; x.font = "52px 'Cormorant Garamond', Georgia, serif";
  wrap(x, text, W - 220).slice(0, 12).forEach((l, i) => x.fillText(l, W / 2, 680 + i * 72));
  x.fillStyle = "#D4AF37"; x.font = "40px Georgia, serif"; x.fillText(link.replace("https://", ""), W / 2, H - 260);
  x.fillStyle = "#A7B0C0"; x.font = "30px Georgia, serif"; x.fillText("Powered by Astra · #ad", W / 2, H - 190);
  return new Promise((r) => c.toBlob(r, "image/png"));
}

export default function CreatorKit({ creator, links }) {
  const [signs, setSigns] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/public/signs").then(({ data }) => setSigns(data.signs)).catch(() => {}); }, []);
  const name = creator.displayName;
  const [round, setRound] = useState(0);
  const captions = dailyCaptions(signs, links.page, creator.handle, round);
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  const download = async (s) => {
    setBusy(s.sign);
    const blob = await drawStory(s.sign, s.overall, name, links.page);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `astra-${s.sign.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.png`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    setBusy("");
  };
  return (
    <section className={cardCls} data-testid="creator-kit">
      <h2 className="text-xl mb-1">Creator Kit</h2>
      <p className="text-sm text-[#A7B0C0] mb-4">Today's 9:16 story cards with your link, plus fresh captions built from today's horoscopes. Both change every day.</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="kit-cards">
        {signs.map((s) => (
          <button key={s.sign} onClick={() => download(s)} disabled={!!busy} data-testid={`kit-card-${s.sign.toLowerCase()}`}
            className="flex items-center justify-center gap-1.5 border border-[#D4AF37]/30 hover:border-[#D4AF37] rounded-xl py-2.5 text-sm transition-colors disabled:opacity-60">
            {busy === s.sign ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} className="gold-text" />} {s.sign}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between mt-6 mb-2">
        <h3 className="text-base">Today's captions</h3>
        <button data-testid="kit-captions-shuffle" onClick={() => setRound((r) => r + 1)} className="flex items-center gap-1.5 text-sm gold-text"><Shuffle size={14} /> Show me others</button>
      </div>
      <div className="space-y-2">
        {captions.map((t, i) => (
          <div key={i} className="flex items-start gap-3 bg-[#0B0F19] border border-[#D4AF37]/15 rounded-xl p-3">
            <p className="flex-1 text-sm text-[#D6D0C5] whitespace-pre-line">{t}</p>
            <button onClick={() => copy(t)} data-testid={`kit-caption-copy-${i}`} aria-label="Copy caption" className="gold-text shrink-0"><Copy size={16} /></button>
          </div>
        ))}
      </div>
      <h3 className="text-base mt-6 mb-2">Disclosure</h3>
      <div className="flex items-start gap-3 bg-[#0B0F19] border border-[#D4AF37]/15 rounded-xl p-3">
        <p className="flex-1 text-sm text-[#D6D0C5]" data-testid="kit-disclosure">{DISCLOSURE} Add #ad at the start of every sponsored post (ASA/FTC).</p>
        <button onClick={() => copy(DISCLOSURE)} data-testid="kit-disclosure-copy" aria-label="Copy disclosure" className="gold-text shrink-0"><Copy size={16} /></button>
      </div>
    </section>
  );
}
